// =============================================================================
// LockedPdfDialog.js, Phase 42.
//
// Calm "your statement is locked" dialog. Stays open across retries so the
// user can correct a wrong password without re-uploading the file.
//
// Props:
//   open: boolean
//   onOpenChange(open)
//   filename: string               , for context (shown in the dialog body)
//   errorMessage: string|null      , set when the previous attempt failed
//                                     (we surface the server's calm copy)
//   busy: boolean                  , parent is currently retrying
//   onSubmit(password: string)     , parent attempts decrypt+parse
//   onCancel()                     , user gave up
// =============================================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function LockedPdfDialog({
  open,
  onOpenChange,
  filename,
  errorMessage = null,
  busy = false,
  onSubmit,
  onCancel,
}) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef(null);

  // Reset password field whenever the dialog re-opens (security hygiene +
  // clear the previous attempt). Keep `showPassword` user preference sticky.
  useEffect(() => {
    if (open) {
      setPassword('');
      // Autofocus after a frame so the dialog has rendered.
      setTimeout(() => { inputRef.current?.focus(); }, 50);
    }
  }, [open]);

  const submit = (e) => {
    e?.preventDefault?.();
    const p = password.trim();
    if (!p) return;
    onSubmit?.(p);
  };

  return (
    <Dialog
      open={!!open}
      onOpenChange={(v) => {
        if (busy) return; // prevent close mid-submit
        onOpenChange?.(v);
        if (!v) onCancel?.();
      }}
    >
      <DialogContent
        className="rounded-2xl sm:max-w-md"
        data-testid="locked-pdf-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display inline-flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            This statement is locked
          </DialogTitle>
          <DialogDescription>
            Your bank sent this PDF with a password. Enter it once and we&rsquo;ll
            unlock the file just to read the transactions.
          </DialogDescription>
        </DialogHeader>

        {filename && (
          <div className="text-[12px] text-muted-foreground rounded-xl border border-border/70 bg-muted/40 p-2.5 truncate" title={filename}>
            {filename}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="locked-pdf-password" className="text-xs text-muted-foreground">
              Password
            </Label>
            <div className="relative">
              <Input
                id="locked-pdf-password"
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter the statement password"
                className="rounded-xl pr-9"
                autoComplete="off"
                spellCheck={false}
                disabled={busy}
                data-testid="locked-pdf-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                data-testid="locked-pdf-toggle-visibility"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {errorMessage && (
            <div
              className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive p-2.5 text-[12px]"
              data-testid="locked-pdf-error"
              role="alert"
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            We unlock the file in memory just to read it, then discard the
            password the moment we&rsquo;re done. It&rsquo;s never stored.
          </p>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              className="rounded-xl"
              onClick={() => {
                if (busy) return;
                onCancel?.();
                onOpenChange?.(false);
              }}
              disabled={busy}
              data-testid="locked-pdf-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="rounded-xl"
              disabled={busy || !password.trim()}
              data-testid="locked-pdf-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Unlocking…
                </>
              ) : (
                'Unlock & continue'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
